import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { GetStaticPaths, GetStaticProps } from "next";
import { Routes } from "~/routes";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import {
    FilterOperators,
    SortDirections,
    ApiQuery,
    FilterMetadata,
    Filter,
} from "~/modules/filters/types";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { AssetIndividualPage } from "~/modules/assets/pages/individual";
import { BreadcrumbItem, Breadcrumbs } from "~/components/Breadcrumbs";
import { env } from "~/env.mjs";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { isNullOrUndefined } from "~/utils/isNullOrUndefined";
import { getValueByAssetClassID } from "~/utils/getValueByAssetClass";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";
import { getAssetURL } from "~/utils/getAssetURL";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetIndividualFaqSEO } from "~/modules/assets/seo/AssetIndividualFaqSEO";
import {
    AssetIndividualWebPageSEO,
    getAssetPageDescription,
} from "~/modules/assets/seo/AssetIndividualWebPageSEO";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { isUnderlyingStock } from "~/utils/isAssetClass";
import { BROADRIDGE_ASSET_TICKER } from "~/consts";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { getAssetIndividualAggregates } from "~/modules/assets/api/getAssetIndividualAggregates";

// // // //

// Assets that should not have individual pages (return 404)
const BLACKLISTED_ASSET_SLUGS = [BROADRIDGE_ASSET_TICKER];

// // // //

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const assetsFilter: Filter = {
            field: AssetColumns.tokenCount,
            operator: FilterOperators.GreaterThan,
            value: 0,
        };

        const PER_PAGE = 250;
        let allAssets: Asset[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
            const assetListQuery: ApiQuery = {
                ...DEFAULT_ASSET_LIST_QUERY,
                name: `asset-individual--${page}`,
                filter: assetsFilter,
                fields: ["id", "slug", "ticker"],
                sort: {
                    direction: SortDirections.asc,
                    field: AssetColumns.ticker,
                },
                pagination: {
                    page,
                    perPage: PER_PAGE,
                },
            };

            const assetListRes = await queryAssets({ query: assetListQuery });
            const assets = assetListRes.results;

            if (!assets || assets.length === 0) {
                hasMore = false;
            } else {
                allAssets = allAssets.concat(assets);
                hasMore = assets.length === PER_PAGE;
                page++;
            }
        }

        const assets = allAssets;

        if (!assets || assets.length === 0) {
            console.warn("No assets found or empty response received");
            return {
                paths: [],
                fallback: "blocking", // Allow fallback to handle missing paths
            };
        }

        // Collect and validate tickers
        const validTickers = assets
            .filter((asset): asset is Asset & { ticker: string } => {
                if (!asset.ticker) {
                    console.warn(`Asset ${asset.id} has no ticker, skipping`);
                    return false;
                }
                return (
                    typeof asset.ticker === "string" && asset.ticker.length > 0
                );
            })
            .map((asset) => asset.ticker);

        // Ensure uniqueness and remove any potential duplicates
        const uniqueTickers = Array.from(new Set(validTickers));

        // Log some statistics for monitoring
        console.info(
            `Generated paths for ${uniqueTickers.length} unique assets (from ${assets.length} total assets)`,
        );

        // Create paths for all valid assets
        const paths = uniqueTickers.map((ticker) => ({
            params: {
                assetSlug: ticker,
            },
        }));

        return {
            paths,
            fallback: "blocking", // Enable ISR for new assets
        };
    } catch (error) {
        // Log the error for monitoring
        console.error("Failed to generate static paths:", error);

        // In production, we want to fail the build to prevent deploying with missing pages
        if (env.NODE_ENV === "production") {
            throw error;
        }

        // In development, return empty paths with fallback to prevent build failures
        return {
            paths: [],
            fallback: "blocking",
        };
    }
};

// // // //

// Helper function to fetch an asset by Asset.ticker
async function getAssetByTicker(assetSlug: string): Promise<Asset | null> {
    // Query for EXACT match of Asset.ticker (includes upper + lowercase)
    const assetResByTickerStrict = await queryAssets({
        query: {
            ...DEFAULT_ASSET_LIST_QUERY,
            filter: {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: AssetColumns.tokenCount,
                        operator: FilterOperators.GreaterThan,
                        value: 0,
                    },
                    {
                        operator: FilterOperators.Or,
                        filters: [
                            {
                                field: AssetColumns.ticker,
                                operator: FilterOperators.Equals,
                                value: assetSlug,
                            },
                            {
                                field: AssetColumns.ticker,
                                operator: FilterOperators.Equals,
                                value: assetSlug.toLowerCase(),
                            },
                            {
                                field: AssetColumns.ticker,
                                operator: FilterOperators.Equals,
                                value: assetSlug.toUpperCase(),
                            },
                        ],
                    },
                ],
            },
        },
    });

    // Prefer an exact-case `ticker` match - distinct assets can have tickers
    // that differ only by case (i.e. `RCOIN` vs `rCOIN`)
    const exactMatch = assetResByTickerStrict.results.find(
        (each) => each.ticker === assetSlug,
    );
    if (exactMatch) {
        return exactMatch;
    }

    // Fall back to case-insensitive `ticker` match (casing is corrected via redirect in getStaticProps)
    const asset = assetResByTickerStrict.results.find(
        (each) => each.ticker?.toLowerCase() === assetSlug.toLowerCase(),
    );

    // Return Asset | null
    return isNullOrUndefined(asset) ? null : asset;
}

// // // //

// Helper function to fetch an asset by Asset.ticker OR Asset.id
async function getAsset(assetSlug: string): Promise<Asset | null> {
    // Check for strict equality by ticker
    const assetByTicker = await getAssetByTicker(assetSlug);
    if (assetByTicker) {
        return assetByTicker;
    }

    // Check for ticker "like" query
    const assetResByTicker = await queryAssets({
        query: {
            ...DEFAULT_ASSET_LIST_QUERY,
            filter: {
                field: AssetColumns.ticker,
                operator: FilterOperators.Like,
                value: assetSlug,
            },
        },
    });

    // Define local asset variable as null
    let asset: Asset | undefined | null = null;

    // Lookup asset by case-insensitive `ticker` property
    asset = assetResByTicker.results.find(
        (each) => each.ticker?.toLowerCase() === assetSlug.toLowerCase(),
    );

    // If asset can't be found by querying `ticker` -> try querying against slug
    if (!asset) {
        const assetResByID = await queryAssets({
            query: {
                ...DEFAULT_ASSET_LIST_QUERY,
                filter: {
                    field: AssetColumns.slug,
                    operator: FilterOperators.Equals,
                    value: assetSlug,
                },
            },
        });

        asset = assetResByID.results[0];
    }

    // If asset can't be found by querying `ticker` -> try querying against ID,
    // but only if the asset slug is a valid number (i.e. not a string)
    if (!asset && !isNaN(Number(assetSlug))) {
        const assetResByID = await queryAssets({
            query: {
                ...DEFAULT_ASSET_LIST_QUERY,
                filter: {
                    field: AssetColumns.id,
                    operator: FilterOperators.Equals,
                    value: assetSlug,
                },
            },
        });

        asset = assetResByID.results[0];
    }

    // Return Asset | null
    return isNullOrUndefined(asset) ? null : asset;
}

// // // //

export const getStaticProps: GetStaticProps<{
    asset: Asset;
    underlyingStockFilterMetadata: FilterMetadata[];
}> = async (ctx) => {
    const assetSlug = ctx.params?.assetSlug as string;

    if (!assetSlug) {
        console.error("Asset slug is missing from params");
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Check if asset is blacklisted
    const isBlacklisted = BLACKLISTED_ASSET_SLUGS.some(
        (slug) => slug.toLowerCase() === assetSlug.toLowerCase(),
    );
    if (isBlacklisted) {
        console.info(`Asset "${assetSlug}" is blacklisted. Returning 404.`);
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Fetch asset — if the API call fails, let it throw.
    // During build: this fails the build (desired).
    // During ISR: Next.js preserves the stale cached page (safer than redirecting).
    // Retries are handled by the lower-level proxyApiQuery/withRetry layer.
    const asset = await getAsset(assetSlug);

    // Asset genuinely not found (API succeeded but returned no result)
    if (asset === null) {
        console.warn(
            `Asset not found for slug "${assetSlug}". Redirecting to home.`,
        );
        return {
            redirect: {
                permanent: false,
                destination: Routes.home,
            },
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Coerce incorrectly cased ticker to proper casing via redirect
    // i.e. `/assets/usdc` -> redirects to -> `/assets/USDC`
    if (asset.ticker && asset.ticker !== assetSlug) {
        // Compare case-insensitively to detect a casing mismatch
        if (asset.ticker.toLowerCase() === assetSlug.toLowerCase()) {
            console.info(
                `Correcting casing for asset slug "${assetSlug}" to "${asset.ticker}"`,
            );
            return {
                redirect: {
                    permanent: true,
                    destination: `/assets/${asset.ticker}`,
                },
            };
        }
    }

    // Safely handle token sorting
    // NOTE - this is a workaround to prevent a SSR render bug resuting from sorting the asset tokens in TokensTable.tsx
    // NOTE - the field + direction match the default sort in useInMemoryAssetSort
    const tokens = Array.isArray(asset.tokens) ? asset.tokens : [];
    let sortedTokens;

    try {
        sortedTokens = sortItemsByProperty({
            items: tokens,
            field: "total_supply_token.val",
            direction: SortDirections.desc,
            nullsLast: true,
        });
    } catch (sortError) {
        console.error(
            `Error sorting tokens for asset "${asset.ticker}":`,
            sortError,
        );
        // Fallback to unsorted tokens if sorting fails
        sortedTokens = tokens;
    }

    const assetWithSortedTokens = {
        ...asset,
        tokens: sortedTokens,
    };

    // Log successful asset fetch for monitoring
    console.info(
        `Successfully generated props for asset "${asset.ticker}" (${asset.id})`,
    );

    // Fetch FilterMetadata for underlying stocks
    let underlyingStockFilterMetadata: FilterMetadata[] = [];
    if (isUnderlyingStock({ asset })) {
        underlyingStockFilterMetadata = await queryAssetFilterMeta({
            filter: {
                field: AssetColumns.peggedAssetID,
                operator: FilterOperators.Equals,
                value: asset.id,
            },
        });
    }

    const aggregates: AggregateDisplayProps[] =
        await getAssetIndividualAggregates(asset);

    return {
        props: {
            asset: assetWithSortedTokens,
            aggregates,
            underlyingStockFilterMetadata: underlyingStockFilterMetadata
                .filter((fm) => {
                    return [
                        AssetColumns.networkIDs,
                        AssetColumns.protocolIDs,
                        AssetColumns.legalStructureCountryID,
                    ].includes(fm.field as AssetColumns);
                })
                // Exclude items with no select options or only one select option
                .filter(
                    (fm) => fm.selectOptions && fm.selectOptions.length > 1,
                ),
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every NEXTJS_REVALIDATE_TIMEOUT seconds
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};

// // // //
// Define breadcrubs for Treasury + Stablecoin products

const TREASURY_PRODUCTS_BREADCRUMB = {
    name: "U.S. Treasury Funds",
    href: Routes.treasuries,
};

const STABLECOINS_BREADCRUMB = {
    name: "Stablecoins",
    href: Routes.stablecoins,
};

const COMMODITIES_BREADCRUMB = {
    name: "Commodities",
    href: Routes.commodities,
};

const PUBLIC_EQUITY_BREADCRUMB = {
    name: "Stocks",
    href: Routes.stocks,
};

const GOVERNMENT_BONDS_BREADCRUMB = {
    name: "Non-U.S. Government Debt",
    href: Routes.governmentBonds,
};

const CREDIT_BREADCRUMB = {
    name: "Credit",
    href: Routes.credit,
};

const REAL_ESTATE_BREADCRUMB = {
    name: "Real Estate",
    href: Routes.realEstate,
};

const PRIVATE_EQUITY_BREADCRUMB = {
    name: "PE / VC",
    href: Routes.privateEquity,
};

const ACTIVE_STRATEGIES_BREADCRUMB = {
    name: "Active Strategies",
    href: Routes.activeStrategies,
};

// // // //

interface Props {
    asset: Asset;
    aggregates: AggregateDisplayProps[];
    underlyingStockFilterMetadata: FilterMetadata[];
}

export default function AssetPage(props: Props) {
    const { asset, aggregates, underlyingStockFilterMetadata } = props;

    const assetClassBreadcrumb = getValueByAssetClassID<BreadcrumbItem>({
        assetClassID: asset.asset_class_id,
        values: {
            stablecoin: STABLECOINS_BREADCRUMB,
            treasury: TREASURY_PRODUCTS_BREADCRUMB,
            commodity: COMMODITIES_BREADCRUMB,
            stocks: PUBLIC_EQUITY_BREADCRUMB,
            non_us_government_debt: GOVERNMENT_BONDS_BREADCRUMB,
            real_estate: REAL_ESTATE_BREADCRUMB,
            active_strategies: ACTIVE_STRATEGIES_BREADCRUMB,
            private_equity: PRIVATE_EQUITY_BREADCRUMB,
            venture_capital: PRIVATE_EQUITY_BREADCRUMB,
            diversified_credit: CREDIT_BREADCRUMB,
            asset_backed_credit: CREDIT_BREADCRUMB,
            specialty_finance: CREDIT_BREADCRUMB,
            municipal_bonds: CREDIT_BREADCRUMB,
            corporate_credit: CREDIT_BREADCRUMB,
            private_credit: CREDIT_BREADCRUMB,
        },
        fallbackValue: STABLECOINS_BREADCRUMB,
    });

    // Construct page title
    const page_title =
        asset.name === asset.ticker
            ? asset.ticker
            : `${asset.name} | ${asset.ticker}`;

    // Build breadcrumbs for UI
    const uiBreadcrumbs: BreadcrumbItem[] = [
        {
            name: "Assets",
        },
        assetClassBreadcrumb,
        {
            name: asset.name,
            icon_url: asset.icon_url,
            current: true,
        },
    ];

    // Build breadcrumbs for next-seo
    let seoBreadcrumbs = [
        {
            name: page_title,
            href: getAssetURL({ asset }),
        },
    ];

    // Add asset class breadcrumb if it exists
    if (assetClassBreadcrumb && assetClassBreadcrumb.href) {
        seoBreadcrumbs.unshift({
            name: assetClassBreadcrumb.name,
            href: assetClassBreadcrumb.href,
        });
    }

    const pageUrl = addBaseUrl(getAssetURL({ asset }));
    const description = getAssetPageDescription({ asset });

    return (
        <PageTrack route={Routes.assetIndividual}>
            <NextSeo
                title={page_title}
                description={description}
                canonical={pageUrl}
                openGraph={{
                    type: "website",
                    url: pageUrl,
                    title: page_title,
                    site_name: "RWA.xyz",
                    description,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: [
                            asset.name,
                            asset.ticker,
                            asset.issuer?.name,
                            asset.asset_class?.name,
                            "RWA",
                            "tokenized assets",
                            "real world assets",
                        ]
                            .filter(Boolean)
                            .join(", "),
                    },
                ]}
            />

            {/* JSON-LD Metadata for SEO */}
            <AssetIndividualWebPageSEO asset={asset} />
            <AssetIndividualFaqSEO asset={asset} />
            <BreadcrumbSEO items={seoBreadcrumbs} />

            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.assetIndividual} user={user} />
                )}
            </UserFetcher>
            {/* NOTE - we might want to abstract these two divs into a dedicated "PageLayout" or "ListPageLayout" component */}
            <SidebarLayout pathname={Routes.assetIndividual}>
                <Breadcrumbs pages={uiBreadcrumbs} />
                <PageWrapper>
                    <AssetIndividualPage
                        asset={asset}
                        aggregates={aggregates}
                        underlyingStockFilterMetadata={
                            underlyingStockFilterMetadata
                        }
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
